import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Avatar, RoleBadge } from "@/components/Brand";
import { Heart, MessageSquare, Pin, Lock, Trash2, Plus, Send, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ADMIN_ROLES = ["super_admin", "trainer"];

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const CAT_COLORS = {
  Announcements: "bg-[#33B6E4]/12 text-[#1779AC] dark:text-[#5cc7ef]",
  "Regulation & Compliance": "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  "Sales & Objections": "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  Wins: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  "Ask the Room": "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

function Composer({ categories, isAdmin, onCreated, onClose }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(categories?.[0] || "Wins");
  const [space, setSpace] = useState("both");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Add a title and some detail first.");
      return;
    }
    setSaving(true);
    try {
      const payload = { title: title.trim(), content: content.trim(), category };
      if (isAdmin) payload.space = space;
      await api.post("/posts", payload);
      toast.success("Posted to the community.");
      onCreated();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-surface mb-6 p-5" data-testid="post-composer">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-[16px] font-semibold">Start a discussion</h3>
        <button onClick={onClose} data-testid="composer-close" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        data-testid="composer-title"
        className="mb-3"
      />
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Share a win, ask a question, or flag something worth discussing…"
        data-testid="composer-content"
        rows={3}
        className="mb-3"
      />
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="composer-category"
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          {(categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {isAdmin && (
          <select
            value={space}
            onChange={(e) => setSpace(e.target.value)}
            data-testid="composer-space"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="both">Everyone</option>
            <option value="partner">Partners only</option>
            <option value="internal">Internal only</option>
          </select>
        )}
        <Button onClick={submit} disabled={saving} data-testid="composer-submit" className="ml-auto">
          <Send className="mr-2 h-4 w-4" /> {saving ? "Posting…" : "Post"}
        </Button>
      </div>
    </div>
  );
}

function CommentThread({ post, isAdmin, currentUserId, onCountChange }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const locked = post.locked;

  const load = () => api.get(`/posts/${post.id}/comments`)
    .then(({ data }) => setComments(data))
    .catch(() => { setComments([]); toast.error("Couldn't load replies."); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [post.id]);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/posts/${post.id}/comments`, { content: text.trim() });
      setText("");
      await load();
      onCountChange(post.id, 1);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-4" data-testid={`comments-${post.id}`}>
      {comments === null ? (
        <Skeleton className="h-12 rounded-lg" />
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No replies yet. Be the first.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2.5" data-testid={`comment-${c.id}`}>
              <Avatar src={c.author?.photo_url} name={c.author?.name} size={30} />
              <div className="min-w-0 flex-1 rounded-lg bg-secondary/60 px-3 py-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold">{c.author?.name}</span>
                  <span className="text-muted-foreground">· {timeAgo(c.created_at)}</span>
                </div>
                <p className="mt-0.5 text-[14px] leading-relaxed text-foreground/90">{c.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {locked && !isAdmin ? (
        <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <Lock className="h-3.5 w-3.5" /> This discussion is locked.
        </p>
      ) : (
        <div className="mt-4 flex items-start gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a reply…"
            rows={1}
            data-testid={`comment-input-${post.id}`}
            className="min-h-[42px] flex-1"
          />
          <Button onClick={add} disabled={saving} size="icon" data-testid={`comment-submit-${post.id}`}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function PostCard({ post, isAdmin, currentUserId, expanded, onToggle, onLike, onPin, onLock, onDelete, onCountChange }) {
  const canDelete = isAdmin || post.author_id === currentUserId;
  return (
    <article className="card-surface p-5" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar src={post.author?.photo_url} name={post.author?.name} size={42} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-semibold">{post.author?.name}</span>
            {post.author?.role && <RoleBadge role={post.author.role} />}
            <span className="text-muted-foreground">· {timeAgo(post.created_at)}</span>
            <div className="ml-auto flex items-center gap-1">
              {isAdmin && (
                <>
                  <button onClick={() => onPin(post)} data-testid={`post-pin-${post.id}`} title={post.pinned ? "Unpin" : "Pin"}
                    className={cn("rounded-md p-1.5 transition-colors hover:bg-accent", post.pinned ? "text-[#33B6E4]" : "text-muted-foreground")}>
                    <Pin className="h-4 w-4" />
                  </button>
                  <button onClick={() => onLock(post)} data-testid={`post-lock-${post.id}`} title={post.locked ? "Unlock" : "Lock"}
                    className={cn("rounded-md p-1.5 transition-colors hover:bg-accent", post.locked ? "text-amber-500" : "text-muted-foreground")}>
                    <Lock className="h-4 w-4" />
                  </button>
                </>
              )}
              {canDelete && (
                <button onClick={() => onDelete(post)} data-testid={`post-delete-${post.id}`} title="Delete"
                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-medium", CAT_COLORS[post.category] || CAT_COLORS["Ask the Room"])}>
              {post.category}
            </span>
            {post.pinned && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#33B6E4]"><Pin className="h-3.5 w-3.5" /> Pinned</span>}
            {post.locked && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Locked</span>}
          </div>
          <h3 className="mt-2 font-heading text-[18px] font-semibold leading-snug">{post.title}</h3>
          <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/85">{post.content}</p>

          <div className="mt-4 flex items-center gap-5 text-sm text-muted-foreground">
            <button
              onClick={() => onLike(post.id)}
              data-testid={`post-like-${post.id}`}
              className={cn("inline-flex items-center gap-1.5 transition-colors hover:text-[#33B6E4]", post.liked && "text-[#33B6E4]")}
            >
              <Heart className={cn("h-[18px] w-[18px]", post.liked && "fill-current")} />
              <span className="tnum">{post.likes}</span>
            </button>
            <button
              onClick={() => onToggle(post.id)}
              data-testid={`post-comments-toggle-${post.id}`}
              className={cn("inline-flex items-center gap-1.5 transition-colors hover:text-[#33B6E4]", expanded && "text-[#33B6E4]")}
            >
              <MessageSquare className="h-[18px] w-[18px]" /> <span className="tnum">{post.comment_count}</span>
              <span className="hidden sm:inline">{expanded ? "Hide" : "Discuss"}</span>
            </button>
          </div>

          {expanded && (
            <CommentThread post={post} isAdmin={isAdmin} currentUserId={currentUserId} onCountChange={onCountChange} />
          )}
        </div>
      </div>
    </article>
  );
}

export default function Community() {
  const { user } = useAuth();
  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("");
  const [composing, setComposing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = (cat) => {
    api.get("/posts", { params: { category: cat || undefined } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load the community feed."));
  };
  useEffect(() => { load(category); /* eslint-disable-next-line */ }, [category]);

  const like = async (id) => {
    try {
      await api.post(`/posts/${id}/like`);
      load(category);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };
  const pin = async (post) => {
    try {
      await api.post(`/posts/${post.id}/pin`);
      load(category);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };
  const lock = async (post) => {
    try {
      const { data: r } = await api.post(`/posts/${post.id}/lock`);
      toast.success(r.locked ? "Post locked." : "Post unlocked.");
      load(category);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };
  const del = async (post) => {
    if (!window.confirm("Delete this post and its replies?")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted.");
      load(category);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };
  const bumpCount = (id, delta) => {
    setData((d) => d && ({ ...d, posts: d.posts.map((p) => p.id === id ? { ...p, comment_count: p.comment_count + delta } : p) }));
  };

  return (
    <div className="fade-in">
      <PageHeader title="Community" subtitle="Discuss what you're learning with the room.">
        <Button onClick={() => setComposing((v) => !v)} data-testid="new-post-btn">
          <Plus className="mr-2 h-4 w-4" /> New post
        </Button>
      </PageHeader>

      {composing && (
        <Composer
          categories={data?.categories}
          isAdmin={isAdmin}
          onCreated={() => { setComposing(false); load(category); }}
          onClose={() => setComposing(false)}
        />
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => setCategory("")}
          data-testid="category-all"
          className={cn("rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
            category === "" ? "border-[#33B6E4] bg-[#33B6E4]/10 text-[#1779AC] dark:text-[#5cc7ef]" : "border-border hover:bg-accent")}
        >
          All posts
        </button>
        {(data?.categories || []).map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`category-${c}`}
            className={cn("rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
              category === c ? "border-[#33B6E4] bg-[#33B6E4]/10 text-[#1779AC] dark:text-[#5cc7ef]" : "border-border hover:bg-accent")}
          >
            {c}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : data.posts.length === 0 ? (
        <div className="card-surface p-10 text-center text-muted-foreground" data-testid="community-empty">
          No posts here yet. Be the first to start a discussion.
        </div>
      ) : (
        <div className="space-y-4" data-testid="community-feed">
          {data.posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              isAdmin={isAdmin}
              currentUserId={user?.id}
              expanded={expandedId === p.id}
              onToggle={(id) => setExpandedId((cur) => cur === id ? null : id)}
              onLike={like}
              onPin={pin}
              onLock={lock}
              onDelete={del}
              onCountChange={bumpCount}
            />
          ))}
        </div>
      )}
    </div>
  );
}
